import Action from "@Actions/action";
import { AnalyticsEvents, AnalyticsProviders } from "@Enums";
import { getAnalyticsEnvironment, hasVerifiedClientVersion, hashEmail } from "@Helpers/analytics-utils";
import { includes } from "lodash-es";
const whiteListedEvents = [
  AnalyticsEvents.user_registered,
  AnalyticsEvents.user_full_access_annual_subscribed,
  AnalyticsEvents.user_full_access_monthly_subscribed,
  AnalyticsEvents.user_authenicated,
  AnalyticsEvents.user_unauthenicated,
  AnalyticsEvents.subscription_plan_selected,
  AnalyticsEvents.b2b_inquiry,
  AnalyticsEvents.access_pin_created,
  AnalyticsEvents.user_login,
  AnalyticsEvents.user_logout,
  AnalyticsEvents.payment_initiated,
  AnalyticsEvents.payment_success,
  AnalyticsEvents.coupon_applied,
  AnalyticsEvents.remove_from_cart,
  AnalyticsEvents.begin_checkout,
  AnalyticsEvents.error_occured,
  AnalyticsEvents.payment_cancelled,
  AnalyticsEvents.payment_exit_reason,
  AnalyticsEvents.verify_email_reported_missing,
  AnalyticsEvents.no_subscriptions_notification,
  AnalyticsEvents.email_verified,
  AnalyticsEvents.portal_page_view,
  AnalyticsEvents.mobile_app_page_view,
  AnalyticsEvents.mobile_app_replay_started,
  AnalyticsEvents.help_improve_survey_answered,
  AnalyticsEvents.help_improve_survey_skipped,
  AnalyticsEvents.feedback_event,
  AnalyticsEvents.new_sign_up,
  AnalyticsEvents.release_note_viewed,
  AnalyticsEvents.subscription_canceled,
  AnalyticsEvents.subscription_keep,
  AnalyticsEvents.volume_adjusted,
  AnalyticsEvents.haptic_strength_adjusted,
  AnalyticsEvents.in_game_feedback_settings_changed,
  AnalyticsEvents.selected_language,
  AnalyticsEvents.guide_played,
  AnalyticsEvents.join_leaderboard_clicked,
  AnalyticsEvents.user_avatar_clicked,
  AnalyticsEvents.positions_selected,
  AnalyticsEvents.training_plan_get_started_clicked,
  AnalyticsEvents.training_plan_not_interested_clicked,
  AnalyticsEvents.training_plan_starting_point_selected,
  AnalyticsEvents.training_plan_frequency_selected,
  AnalyticsEvents.training_plan_enrollment_submitted,
  AnalyticsEvents.training_plan_entry_clicked,
  AnalyticsEvents.training_plan_edit_clicked,
  AnalyticsEvents.training_plan_opened,
  AnalyticsEvents.analyze_my_performance_clicked,
  AnalyticsEvents.how_to_improve_clicked,
  AnalyticsEvents.metric_details_clicked,
  AnalyticsEvents.metric_details_video_played,
  AnalyticsEvents.practice_settings_adjusted,
  AnalyticsEvents.matchplay_settings_adjusted,
  AnalyticsEvents.matchplay_position_updated,
  AnalyticsEvents.matchplay_started,
  AnalyticsEvents.practice_started,
  AnalyticsEvents.challenge_started,
  AnalyticsEvents.career_started,
  AnalyticsEvents.results_scenario_clicked,
  AnalyticsEvents.results_replay_scenario_clicked,
  AnalyticsEvents.results_overall_clicked,
  AnalyticsEvents.scenario_switched,
  AnalyticsEvents.decision_engine_rated,
  AnalyticsEvents.replay_2d_started,
  AnalyticsEvents.replay_3d_opened,
  AnalyticsEvents.alternative_decision_selected,
  AnalyticsEvents.decision_score_info_popup_opened,
  AnalyticsEvents.decision_score_panel_toggled,
  AnalyticsEvents.vision_cone_toggled,
  AnalyticsEvents.heatmap_toggled,
  AnalyticsEvents.leaderboard_status_change,
  AnalyticsEvents.leaderboard_anonymity_change,
  AnalyticsEvents.page_navigated,
  AnalyticsEvents.challenge_see_all_results_clicked,
  AnalyticsEvents.challenge_preview_play_clicked,
  AnalyticsEvents.video_player_play_challenge_clicked,
  AnalyticsEvents.skip_bootcamp_clicked,
  AnalyticsEvents.keep_training_clicked,
  AnalyticsEvents.demo_sign_up,
  AnalyticsEvents.demo_account_expired_notification_shown,
  AnalyticsEvents.demo_account_expired_notification_go_to_purchase_clicked,
  AnalyticsEvents.demo_account_expired_notification_sign_out_clicked,
  AnalyticsEvents.demo_account_status_go_to_purchase_clicked,
  AnalyticsEvents.demo_account_ended,
  AnalyticsEvents.demo_account_end_failed,
  AnalyticsEvents.subscription_updated,
  AnalyticsEvents.subscription_canceled,
  AnalyticsEvents.subscription_keep,
];

//used for (GA) data analytics events
const dataLayerPush = (eventName, data = {}) => {
  window.dataLayer = window.dataLayer || [];
  const eventObj = { event: eventName, ...data };
  window.dataLayer.push(eventObj);
};

// used for (GA) ecommerce events, ensure that the data is structured correctly for ecommerce events when using this function
const dataLayerEcommercePush = (eventName, data = {}) => {
  window.dataLayer = window.dataLayer || [];
  const eventObj = {
    event: eventName,
    ecommerce: {
      price_id: data.priceId,
      currency: data.currency.toUpperCase(),
      value: data.amount,
      items: [
        {
          item_id: data.priceId,
          price: data.price,
          quantity: data.quantity,
        },
      ],
    },
  };
  window.dataLayer.push(eventObj);
};

const handleMeta = (eventType, data, state) => {
  try {
    if (queueEventIfUnauthenticated(eventType, data, AnalyticsProviders.META, state)) {
      return;
    }

    if (window.fbq) {
      switch (eventType) {
        case AnalyticsEvents.subscription_plan_selected:
          fbq("track", "AddToCart", { product_id: data.package });
          break;
        case AnalyticsEvents.user_registered:
          fbq("track", "CompleteRegistration");
          break;
        case AnalyticsEvents.payment_initiated:
          fbq("track", "InitiateCheckout", { product_id: data.package });
          break;
        case AnalyticsEvents.payment_success:
          fbq("track", "Purchase", {
            value: data.amount,
            currency: data.currency.toUpperCase(),
          });
          break;
        case AnalyticsEvents.b2b_inquiry:
          fbq("track", "Contact");
          break;
      }
    }
  } catch (error) {
    console.error("Meta analytics failed:", error);
  }
};

const handleGA = (eventType, data, state) => {
  try {
    if (queueEventIfUnauthenticated(eventType, data, AnalyticsProviders.GA, state)) {
      return;
    }

    switch (eventType) {
      case AnalyticsEvents.subscription_plan_selected:
        break;
      case AnalyticsEvents.user_registered:
        break;
      case AnalyticsEvents.payment_initiated:
        break;
      case AnalyticsEvents.payment_success:
        dataLayerEcommercePush("purchase", { ...data });
        break;
      case AnalyticsEvents.remove_from_cart:
        dataLayerEcommercePush("remove_from_cart", { ...data });
        break;
      case AnalyticsEvents.begin_checkout:
        dataLayerEcommercePush("begin_checkout", { ...data });
        break;
      case AnalyticsEvents.b2b_inquiry:
        break;
    }
  } catch (error) {
    console.error("Google Analytics failed:", error);
  }
};

const handleSnap = (eventType, data, state) => {
  try {
    if (queueEventIfUnauthenticated(eventType, data, AnalyticsProviders.SNAP, state)) {
      return;
    }

    if (window.snaptr) {
      switch (eventType) {
        case AnalyticsEvents.subscription_plan_selected:
          window.snaptr("track", "ADD_CART", {
            item_id: data.package,
            item_category: "subscription",
            price: data.price || 0,
            currency: data.currency?.toUpperCase() || "USD",
            email_hash: hashEmail(state.user.email),
          });
          break;
        case AnalyticsEvents.page_navigated:
          window.snaptr("track", "PAGE_VIEW", {
            page_name: data.page || window.location.pathname,
            email_hash: hashEmail(state.user.email),
          });
          break;
        case AnalyticsEvents.payment_success:
          window.snaptr("track", "PURCHASE", {
            item_id: data.priceId,
            item_category: "subscription",
            price: data.amount || data.price || 0,
            currency: data.currency?.toUpperCase() || "USD",
            email_hash: hashEmail(state.user.email),
          });
          break;
        case AnalyticsEvents.user_registered:
          window.snaptr("track", "SIGN_UP", {
            email_hash: hashEmail(state.user.email),
          });
          break;
        case AnalyticsEvents.payment_initiated:
          window.snaptr("track", "START_CHECKOUT", {
            item_id: data.package,
            item_category: "subscription",
            price: data.price || 0,
            currency: data.currency?.toUpperCase() || "USD",
            email_hash: hashEmail(state.user.email),
          });
          break;
      }
    } else {
    }
  } catch (error) {
    console.error("Snapchat analytics failed:", error);
  }
};

// Bing UET custom-event payload shape aligns with Microsoft UET customData / CAPI fields:
// https://learn.microsoft.com/en-us/advertising/guides/uet-conversion-api-integration?view=bingads-13
const bingUetCurrency = data => (data?.currency || "USD").toString().toUpperCase();

const handleBing = (eventType, data, state) => {
  try {
    if (queueEventIfUnauthenticated(eventType, data, AnalyticsProviders.BING, state)) {
      return;
    }

    if (!window.uetq) {
      return;
    }

    const currency = bingUetCurrency(data);

    switch (eventType) {
      case AnalyticsEvents.payment_success: {
        const value = Number(data.amount ?? data.price) || 0;

        const qty = Number(data.quantity) > 0 ? Number(data.quantity) : 1;
        const unitPrice = Number(data.price) || (qty ? value / qty : value);
        const priceId = data.priceId;
        const item = priceId
          ? {
              id: priceId,
              quantity: qty,
              price: unitPrice,
              name: priceId,
            }
          : null;
        const purchasePayload = {
          eventCategory: "subscription",
          eventLabel: priceId,
          eventValue: value,
          pageType: "purchase",
          value,
          currency,
          ecommTotalValue: value,
          ...(priceId ? { itemIds: [priceId], items: [item] } : {}),
        };
        if (data.transactionId) {
          purchasePayload.transactionId = String(data.transactionId);
        }
        window.uetq.push("event", "purchase", purchasePayload);
        break;
      }
    }
  } catch (error) {
    console.error("Bing analytics failed:", error);
  }
};

let analyticsQueue = [];
let isProcessingQueue = false;

const queueEventIfUnauthenticated = (eventType, eventData, handler, state) => {
  if (!state.user.authenticated || !state.user.id) {
    analyticsQueue.push({
      eventType,
      eventData,
      handler,
    });
    return true;
  }
  return false;
};

const processAnalyticsQueue = state => {
  if (isProcessingQueue || !state.user.authenticated || !state.user.id) {
    return;
  }

  isProcessingQueue = true;

  analyticsQueue.forEach(({ eventType, eventData, handler }) => {
    switch (handler) {
      case AnalyticsProviders.META:
        handleMeta(eventType, eventData, state);
        break;
      case AnalyticsProviders.GA:
        handleGA(eventType, eventData, state);
        break;
      case AnalyticsProviders.SNAP:
        handleSnap(eventType, eventData, state);
        break;
      case AnalyticsProviders.BING:
        handleBing(eventType, eventData, state);
        break;
    }
  });

  analyticsQueue = [];
  isProcessingQueue = false;
};

window.processAnalyticsQueue = processAnalyticsQueue;

export default class AnalyticsEvent extends Action {
  runAction() {
    const eventType = this._payload.type;

    if (!eventType) {
      return console.warn("No event type included in analytics event", this._payload);
    }

    const eventData = this._payload.data;
    const eventEnvironment = getAnalyticsEnvironment(this._state);
    const eventUserRating = this._state.user.userRating;
    const isBusiness = Boolean(this._state.account?.is_business);

    let combinedEventData = {
      ...eventData,
      environment: eventEnvironment,
      user_rating: eventUserRating,
      is_business: isBusiness,
      domain: window.location.hostname,
    };

    if (hasVerifiedClientVersion(this._state.app.clientVersion)) {
      combinedEventData.client_version = this._state.app.clientVersion;
    }

    if (!includes(whiteListedEvents, eventType)) {
      console.log("event not in whitelist", eventType);
      return;
    }

    this._amplitude?.logEvent(eventType, combinedEventData);

    handleMeta(eventType, eventData, this._state);
    handleGA(eventType, eventData, this._state);
    handleSnap(eventType, eventData, this._state);
    handleBing(eventType, eventData, this._state);

    this.onSuccess();
  }
}
